import AsyncStorage from "@react-native-async-storage/async-storage";
import { createStackNavigator } from "@react-navigation/stack";
import { useEffect, useState } from "react";
import { TextInput } from "react-native";
import { batch, useDispatch } from "react-redux";
import { setAuthToken } from "../actions/AuthAction";
import {
  setAppInitialized,
  setUserAuthenticated,
} from "../actions/mainActions";
import { AuthenticateCustomerUserReqPayload } from "../central-atoms/users/types/requests";
import HttpRequestService from "../config/HttpRequestService";
import { APP_VARIANT, CENTRAL_HOST } from "../config/setting";
import { GLOBAL_VARS, LS_KEYS } from "../constants";
import { sleep } from "../functions/time";
import { getGlobalVar, setGlobalVar, setInitialRoute, getInitialRoute } from "../helpers";
import {
  useAuthToken,
  useCentralUserProfile,
  useSelectedLanguage,
} from "../hooks";
import i18n from "../lang/i18n";
import AuthAction from "../redux/reducer/auth/actions";
import AuthenticatedUserRequestService from "../services/AuthenticatedUserRequestService";
import AuthenticationRequestService from "../services/AuthenticationRequestService";
import AuthenticationService from "../services/AuthenticationService";
import { ErrorHandlerService } from "../services/ErrorHandlerService";
import { THEME_COLORS } from "../theme/constants/themeColors";
import { AuthStack } from "./stacks/AuthStack";
import { MainStack } from "./stacks/MainStack";
import { AvailableAppVariants } from "../theme/enums";
import RemoteConfigService from "../org-app-config/services/RemoteConfigService";

// Remove font scale
(Text as any).defaultProps = (Text as any).defaultProps || {};
(Text as any).defaultProps.allowFontScaling = false;
(TextInput as any).defaultProps = (TextInput as any).defaultProps || {};
(TextInput as any).defaultProps.allowFontScaling = false;

export default function NavStart() {
  // const navigationRef = useNavigationContainerRef();
  const Stack = createStackNavigator();
  const [loading, setLoading] = useState(true);
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const dispatch = useDispatch();

  const centralUserProfile = useCentralUserProfile();

  const authToken = useAuthToken();
  const { setCurrentLanguage, setPartialLogin } = AuthAction;

  const remoteConfigInstance: RemoteConfigService = getGlobalVar(GLOBAL_VARS.RemoteConfigInstance);

  const selectedLanguage = useSelectedLanguage();

  const setAuthTokenFromEncryptedString = async () => {
    /**
     * - Extract the token from the URL
     * - Make a fetch request to a backend endpoint which creates a user by reading the encrypted string
     * - The API returns the token 
     */

    // const encryptedData = window.location.search;
    const params = new URLSearchParams(window.location.search);
    const encryptedData = params.get("ed");
    
    // console.log('encryptedData', encryptedData);

    if (!encryptedData) {
      return;
    }

    const data: AuthenticateCustomerUserReqPayload = {
      encryptedData,
    };

    try {
      const response = await AuthenticationRequestService.authenticateCustomersUser(data)
      await AuthenticationService.setAuthToken(response.data.token);

      // console.log('response', response);

      if (!!response.data.primary_cs_id) {
        await AsyncStorage.setItem(LS_KEYS.deepLinkCSId, response.data.primary_cs_id)
          .catch((e) => {
            ErrorHandlerService.handleError(e, {
              openToast: false,
            });
          });
      }
    }
    catch (e) {
      ErrorHandlerService.handleError(e);
    }
  }

  const initialize = async () => {
    try {
      if (!remoteConfigInstance) {
        const remoteConfigInstance = new RemoteConfigService();
        setGlobalVar(GLOBAL_VARS.RemoteConfigInstance, remoteConfigInstance);
      }

      const language = await AsyncStorage.getItem(LS_KEYS.activeLanguage);
      if (language) {
        await i18n.changeLanguage(language);
        dispatch(setCurrentLanguage(language));
      }

      // Get the encrypted string and set the token
      if (APP_VARIANT === AvailableAppVariants.TataMotorsLite) {
        await setAuthTokenFromEncryptedString();
      }

      const token = await AuthenticationService.getAuthToken();
      const paymentMode = await AsyncStorage.getItem(LS_KEYS.paymentMode);
      const enteredFleetCode = await AsyncStorage.getItem(LS_KEYS.enteredFleetCode);
      const enteredAccessCode = await AsyncStorage.getItem(LS_KEYS.enteredAccessCode);

      // For deep linking /*
      const route = window.location.pathname;
      const fullPath = `${route}${window.location.search}`;

      if (route) {
        setInitialRoute(fullPath);
      }

      // Store the intended route if user is not authenticated
      if (typeof token !== "string" && route) {
        await AsyncStorage.setItem(LS_KEYS.intendedRoute, fullPath);
      }
      // For deep linking */

      if (typeof token === "string") {
        dispatch(setAuthToken(token));
        dispatch(setPartialLogin(paymentMode === null));

        await sleep(100);

        try {
          const { data } = await AuthenticatedUserRequestService.fetchDriverUserProfile();

          AuthenticationService.login(dispatch, {
            centralUser: data.central_user,
            primaryUser: data.user,
            fleetCode: enteredFleetCode,
            accessCode: enteredAccessCode,
            paymentMode: paymentMode,
            selectedLanguage: selectedLanguage,
          });
          setUserProfileData(data);
          setLoading(false);

          // After successful login, check for stored intended route
          const intendedRoute = await AsyncStorage.getItem(LS_KEYS.intendedRoute);
          if (intendedRoute) {
            // set intendedRoute as initialRoute
            setInitialRoute(intendedRoute);
            // and clear intendedRoute
            await AsyncStorage.removeItem(LS_KEYS.intendedRoute);
          }
        } 
        catch (e: any) {
          if (e?.response?.status === 401) {
            console.log("Logging out...");
            AuthenticationService.logout(dispatch);
          }

          ErrorHandlerService.handleError(e, {
            toastOptions: {
              type: "error",
            },
            openToast: false,
          });
          setLoading(false);
          setUserProfileData(null);
        }
      }
      else {
        batch(() => {
          dispatch(setAuthToken(null));
          dispatch(setUserAuthenticated(false));
          dispatch(setPartialLogin(null));
          dispatch(setAppInitialized(true));
        });
        setLoading(false);
        setUserProfileData(null);
      }

      await sleep(100);
    }
    catch (e) {
      AuthenticationService.logout(dispatch);
      ErrorHandlerService.handleError(e, {
        toastOptions: {
          type: "error",
        },
      });
      setLoading(false);
      setUserProfileData(null);
    }
  };

  useEffect(() => {
    const httpRequestInstance = new HttpRequestService();
    const centralHttpRequestInstance = new HttpRequestService();
    if (authToken) httpRequestInstance.setInstance(authToken);
    else httpRequestInstance.setInstance();

    if (centralUserProfile?.central_token) {
      centralHttpRequestInstance.setCustomInstance({
        authToken: centralUserProfile?.central_token,
        config: {
          baseURL: CENTRAL_HOST,
        },
      });
    } else {
      centralHttpRequestInstance.setCustomInstance({
        config: {
          baseURL: CENTRAL_HOST,
        },
      });
    }
    setGlobalVar(GLOBAL_VARS.httpRequest, httpRequestInstance);
    setGlobalVar(GLOBAL_VARS.centralHttpRequest, centralHttpRequestInstance);
  }, [authToken, centralUserProfile?.central_token]);

  useEffect(() => {
    if (remoteConfigInstance) {
      remoteConfigInstance.initialize();
    }
  }, [remoteConfigInstance, centralUserProfile?.central_token]);

  useEffect(() => {
    const httpRequestInstance = new HttpRequestService();
    if (authToken) httpRequestInstance.setInstance(authToken);
    else httpRequestInstance.setInstance();

    setGlobalVar(GLOBAL_VARS.httpRequest, httpRequestInstance);
  }, [authToken]);

  useEffect(() => {
    initialize();
  }, []);

  // console.log('initialRoute', loading, getInitialRoute());

  return loading ? null : (
    <Stack.Navigator
      initialRouteName={
        loading || !userProfileData || userProfileData === null 
          ? "AuthStack" 
          : "MainStack"
      }
      screenOptions={{
        cardStyle: {
          backgroundColor: THEME_COLORS.bgColor,
        },
      }}
    >
      <Stack.Screen
        name="MainStack"
        component={MainStack}
        options={{ 
          headerShown: false,
          cardStyle: {
            backgroundColor: THEME_COLORS.bgColor,
          },
        }}
      />
      <Stack.Screen
        name="AuthStack"
        component={AuthStack}
        options={{ 
          headerShown: false,
          cardStyle: {
            backgroundColor: THEME_COLORS.bgColor,
          },
        }}
      />
    </Stack.Navigator>
  );
}
